import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { companiesQueryOptions } from "@/auth/queryOptions";

/**
 * Pathless layout for every per-company route. Resolves the URL slug
 * to an actual company row, exposes it to children via the route
 * context (`Route.useRouteContext()` returns `{ company }`), and
 * persists `lastCompanySlug` to localStorage so `/` can land back here
 * on the next visit.
 *
 * Unknown slug → bounce to `/` which then picks a valid landing.
 */
export const Route = createFileRoute("/_authed/$companySlug")({
  beforeLoad: async ({ context, params, location }) => {
    const data = await context.queryClient.ensureQueryData(companiesQueryOptions);
    const company = data.companies.find((c) => c.slug === params.companySlug);
    if (!company) throw redirect({ to: "/" });
    if (typeof window !== "undefined") {
      localStorage.setItem("lastCompanySlug", params.companySlug);
    }
    // Unlicensed tenants are walled off behind /<slug>/license. The
    // stub page itself is exempt — otherwise the user couldn't reach
    // the recheck / paste affordances to fix the problem. When
    // LICENSING_ENABLED=false on the server, every company has
    // licensed:true and this branch is dead.
    if (!company.licensed && !location.pathname.endsWith("/license")) {
      throw redirect({
        to: "/$companySlug/license",
        params: { companySlug: params.companySlug },
      });
    }
    // `isAdmin` is filled by /api/auth/companies based on the caller's
    // IDS adminDomains. Nested routes pick it up via
    // `Route.useRouteContext()` to hide / gate admin-only chrome.
    return { company, isAdmin: company.isAdmin };
  },
  component: CompanyLayout,
});

function CompanyLayout() {
  return <Outlet />;
}
